import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { useAPI } from './APIContext';
import { useAuth } from './AuthContext';
import MathChallengeModal from '../components/MathChallengeModal';
import ProfileSwitchAnimation from '../components/ProfileSwitchAnimation';
import { useAnalytics } from '../hooks/useAnalytics';

const ProfileContext = createContext();

export const useProfile = () => {
  const context = useContext(ProfileContext);
  if (!context) {
    throw new Error('useProfile must be used within ProfileProvider');
  }
  return context;
};

export const ProfileProvider = ({ children }) => {
  const { trackProfileSwitched } = useAnalytics();
  const { apiClient } = useAPI();
  const { user } = useAuth();
  const [profiles, setProfiles] = useState([]);
  const [currentProfile, setCurrentProfile] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showMathChallenge, setShowMathChallenge] = useState(false);
  const [mathChallengeAction, setMathChallengeAction] = useState('');
  const [mathChallengeCallback, setMathChallengeCallback] = useState(null);

  // Animation state
  const [showSwitchAnimation, setShowSwitchAnimation] = useState(false);
  const [animationOldProfile, setAnimationOldProfile] = useState(null);
  const [animationNewProfile, setAnimationNewProfile] = useState(null);

  // Load profiles from API
  const loadProfiles = useCallback(async () => {
    if (!user?.id) {
      setProfiles([]);
      setCurrentProfile(null);
      setIsLoading(false);
      return;
    }

    try {
      setIsLoading(true);
      setError(null);

      const response = await apiClient.getProfiles();

      if (response?.status && response?.data) {
        const profilesList = Array.isArray(response.data) ? response.data : [];
        setProfiles(profilesList);

        // Try to restore previously selected profile from localStorage
        const savedProfileId = localStorage.getItem(`selectedProfile_${user.id}`);
        let profileToSelect = null;

        if (savedProfileId) {
          profileToSelect = profilesList.find(p => p.id === savedProfileId);
        }

        // If no saved profile or saved profile not found, use default profile
        if (!profileToSelect) {
          profileToSelect = profilesList.find(p => p.is_default) || profilesList[0];
        }

        if (profileToSelect) {
          setCurrentProfile(profileToSelect);
          localStorage.setItem(`selectedProfile_${user.id}`, profileToSelect.id);
        }
      } else {
        throw new Error('Failed to load profiles');
      }
    } catch (err) {
      console.error('Error loading profiles:', err);
      setError(err.message || 'Failed to load profiles');

      // If we fail to load profiles, try to get/create default profile
      try {
        const defaultResponse = await apiClient.getDefaultProfile();
        if (defaultResponse?.status && defaultResponse?.data) {
          const defaultProfile = defaultResponse.data;
          setProfiles([defaultProfile]);
          setCurrentProfile(defaultProfile);
          localStorage.setItem(`selectedProfile_${user.id}`, defaultProfile.id);
        }
      } catch (defaultErr) {
        console.error('Error loading default profile:', defaultErr);
      }
    } finally {
      setIsLoading(false);
    }
  }, [apiClient, user]);

  // Switch to a different profile
  const switchProfile = useCallback((profileId) => {
    const newProfile = profiles.find(p => p.id === profileId);
    if (newProfile && newProfile.id !== currentProfile?.id) {
      trackProfileSwitched(currentProfile?.id, profileId);
      // Trigger animation
      setAnimationOldProfile(currentProfile);
      setAnimationNewProfile(newProfile);
      setShowSwitchAnimation(true);

      // Sync API header source immediately to avoid one-render lag/race where
      // pages re-fetch before the separate effect updates the API client.
      apiClient.setActiveProfileId(newProfile.id);

      // Switch immediately so profile-scoped pages (e.g. Library) re-fetch right away.
      setCurrentProfile(newProfile);
      if (user?.id) {
        localStorage.setItem(`selectedProfile_${user.id}`, newProfile.id);
      }

      // Dispatch custom event to notify other components of profile change
      window.dispatchEvent(new CustomEvent('profileChanged', { detail: newProfile }));
    }
  }, [profiles, user, currentProfile, apiClient]);

  // Refresh profiles list
  const refreshProfiles = useCallback(async () => {
    await loadProfiles();
  }, [loadProfiles]);

  // Check if current profile is a child profile
  const isChildProfile = useCallback(() => {
    return currentProfile?.is_child === true;
  }, [currentProfile]);

  // Require parental verification for protected actions
  const requireParentalVerification = useCallback((action, callback) => {
    console.log('🔒 requireParentalVerification called', {
      action,
      isChild: currentProfile?.is_child,
      profileName: currentProfile?.name
    });

    if (!isChildProfile()) {
      // Not a child profile, execute immediately
      console.log('✅ Not a child profile, executing immediately');
      callback();
      return;
    }

    console.log('🔐 Child profile detected, showing math challenge');
    // Show math challenge
    setMathChallengeAction(action);
    // Store a function that will execute the callback when called
    setMathChallengeCallback(() => () => callback());
    setShowMathChallenge(true);
  }, [isChildProfile, currentProfile]);

  // Handle successful math challenge
  const handleMathChallengeSuccess = useCallback(() => {
    console.log('✅ Math challenge passed, executing callback');
    if (mathChallengeCallback) {
      mathChallengeCallback(); // This now executes the callback
      setMathChallengeCallback(null);
    }
    setShowMathChallenge(false);
  }, [mathChallengeCallback]);

  // Load profiles when user changes
  useEffect(() => {
    loadProfiles();
  }, [loadProfiles]);

  // Keep API client in sync with currently selected profile so all
  // profile-scoped requests automatically include X-Profile-Id.
  useEffect(() => {
    if (!apiClient) return;
    apiClient.setActiveProfileId(currentProfile?.id || null);
  }, [apiClient, currentProfile?.id]);

  const value = {
    profiles,
    currentProfile,
    isLoading,
    error,
    switchProfile,
    refreshProfiles,
    isChildProfile,
    requireParentalVerification,
  };

  return (
    <ProfileContext.Provider value={value}>
      {children}
      <MathChallengeModal
        isOpen={showMathChallenge}
        onClose={() => setShowMathChallenge(false)}
        onSuccess={handleMathChallengeSuccess}
        action={mathChallengeAction}
      />
      {showSwitchAnimation && (
        <ProfileSwitchAnimation
          oldProfile={animationOldProfile}
          newProfile={animationNewProfile}
          onComplete={() => {
            setShowSwitchAnimation(false);
            setAnimationOldProfile(null);
            setAnimationNewProfile(null);
          }}
        />
      )}
    </ProfileContext.Provider>
  );
};
